import { useEffect, useMemo } from "react";
import { t } from "@/i18n-js/instance";
import type {
  PaywallCoupon,
  PaywallCouponStatus,
} from "@/react/types/PaywallCoupon";
import { PAYWALL_COUPON_STATUSES } from "@circle-react/helpers/paywallCouponHelpers";
import { usePaywallCouponList } from "@circle-react/hooks/paywalls/usePaywallCouponList";
import { useCreatePaywallSubscriptionFormContext } from "../CreatePaywallSubscriptionFormContextProvider";

const noCouponCodeStringDefault = t("add_paywall_coupon_modal.select_coupon");
export const couponStatusesDefault = PAYWALL_COUPON_STATUSES;

interface CouponOption {
  label: string;
  value: string;
}

interface UseCouponFormParams {
  couponStatuses?: PaywallCouponStatus[];
  paywallId: number | undefined;
  selectCouponString?: string;
  skipDateValidations?: boolean;
}

interface UseCouponFormReturn {
  couponCodeOptions: CouponOption[];
  paywallCouponsListQuery: ReturnType<
    typeof usePaywallCouponList
  >["paywallCouponsListQuery"];
}

export const useCouponForm = ({
  paywallId,
  selectCouponString = noCouponCodeStringDefault,
  couponStatuses = couponStatusesDefault,
  skipDateValidations = true,
}: UseCouponFormParams): UseCouponFormReturn => {
  const { paywallCouponsListQuery } = usePaywallCouponList({
    paywallId,
    couponStatuses,
    skipDateValidations,
  });

  const [couponCodeOptions, couponMappedByCouponCode] = useMemo(() => {
    const couponsRaw = paywallCouponsListQuery?.data || [];
    const couponCodeOptions: CouponOption[] = [
      { label: selectCouponString, value: "" },
    ];
    const couponMappedByCouponCode: Record<string, PaywallCoupon> = {};

    couponsRaw.forEach(coupon => {
      couponCodeOptions.push({
        label: coupon.code,
        value: coupon.code,
      });
      couponMappedByCouponCode[coupon.code] = coupon;
    });

    return [couponCodeOptions, couponMappedByCouponCode];
  }, [selectCouponString, paywallCouponsListQuery.data]);

  const { setCouponsIndex } = useCreatePaywallSubscriptionFormContext();

  useEffect(() => {
    setCouponsIndex?.(couponMappedByCouponCode ?? {});
  }, [couponMappedByCouponCode, setCouponsIndex]);

  return {
    paywallCouponsListQuery,
    couponCodeOptions,
  };
};
